import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CreditCard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

type BillingSummary = {
  currentPlan?: {
    displayName?: string | null;
    basePriceCents?: number | null;
    billingCycle?: string | null;
    billingStatus?: string | null;
  } | null;
};

// Full-screen paywall shown when the current workspace has been suspended for
// non-payment. The server hard-blocks every non-exempt API call with a 402
// (`code: "BILLING_SUSPENDED"`); queryClient broadcasts a `billing-suspended`
// window event, which this gate listens for. Workspace data is untouched — the
// admin just needs to settle the balance to restore access.
//
// The gate suppresses itself when the user is already on /account/billing so
// that page can actually render and accept payment — without this guard, the
// page's own non-exempt API calls would re-trigger the gate on top of itself,
// creating an unescapable loop.
export function BillingSuspendedGate() {
  const [suspended, setSuspended] = useState(false);

  useEffect(() => {
    const onSuspended = () => {
      // Never overlay the Platform Admin — it is never workspace-scoped and
      // has no billing concept. A suspended tenant workspace must not block
      // the super-admin surface.
      if (window.location.pathname.startsWith("/platform")) return;
      // Don't overlay the billing page itself — the user is already in the
      // right place to pay. Let /account/billing render normally.
      if (window.location.pathname === "/account/billing") return;
      setSuspended(true);
    };
    window.addEventListener("billing-suspended", onSuspended);
    return () => window.removeEventListener("billing-suspended", onSuspended);
  }, []);

  // Dismiss if user navigates to /account/billing or /platform/* while the gate is showing.
  useEffect(() => {
    if (!suspended) return;
    const onNavAway = () => {
      const p = window.location.pathname;
      if (p === "/account/billing" || p.startsWith("/platform")) setSuspended(false);
    };
    window.addEventListener("popstate", onNavAway);
    return () => window.removeEventListener("popstate", onNavAway);
  }, [suspended]);

  // Safety net: if the gate is somehow already showing and the user is on a
  // platform route (e.g. SPA navigation after the gate was triggered), dismiss.
  useEffect(() => {
    if (suspended && window.location.pathname.startsWith("/platform")) {
      setSuspended(false);
    }
  }, [suspended]);

  // /api/account-billing/summary stays reachable while suspended (exempt path),
  // so we can show what's owed AND confirm the suspension is still real. If the
  // active workspace is no longer suspended (e.g. context switched, or payment
  // landed in another tab), self-dismiss instead of staying stuck.
  const { data } = useQuery<BillingSummary>({
    queryKey: ["/api/account-billing/summary"],
    enabled: suspended,
    retry: false,
  });

  const reportedStatus = data?.currentPlan?.billingStatus;
  useEffect(() => {
    if (suspended && reportedStatus && reportedStatus !== "suspended") {
      setSuspended(false);
    }
  }, [suspended, reportedStatus]);

  if (!suspended) return null;

  const cents = data?.currentPlan?.basePriceCents;
  const amount = typeof cents === "number" ? cents / 100 : undefined;
  const planName = data?.currentPlan?.displayName;
  const cycleLabel = data?.currentPlan?.billingCycle === "annual" ? "yr" : "mo";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      data-testid="overlay-billing-suspended"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-7 shadow-xl">
        <div className="mb-4 flex items-center gap-2">
          <AlertTriangle className="h-7 w-7 flex-shrink-0 text-amber-500" />
          <h1
            className="text-xl font-bold text-foreground"
            data-testid="text-billing-suspended-title"
          >
            Workspace suspended
          </h1>
        </div>
        <p
          className="mb-4 text-sm leading-relaxed text-muted-foreground"
          data-testid="text-billing-suspended-message"
        >
          This workspace is suspended for non-payment
          {planName ? <> of the <strong>{planName}</strong> plan</> : null}
          {typeof amount === "number" && amount > 0 ? (
            <> (${amount.toFixed(2)}/{cycleLabel})</>
          ) : null}
          . Your data is safe and untouched — a workspace admin just needs to
          settle the outstanding balance to restore access.
        </p>
        <div className="flex flex-col gap-2">
          <Button
            className="w-full"
            onClick={() => {
              window.location.href = "/account/billing";
            }}
            data-testid="button-billing-settle"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Settle balance &amp; restore access
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              window.location.href = "/api/logout";
            }}
            data-testid="button-billing-logout"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
